import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { EggProductionRecord } from '@henlytics/types'
import { Count } from '../components/common/Money'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { DateCell } from '../components/entry/DateCell'
import { EntryGrid } from '../components/entry/EntryGrid'
import { NumericCell } from '../components/entry/NumericCell'
import { useBatches } from '../hooks/useBatches'
import { useProduction, useRecordProduction, useVoidProduction } from '../hooks/useDailyEntry'
import { useCreateFeedIssue, useMaterials } from '../hooks/useInventory'
import { today } from '../hooks/useReports'
import { clientUuid } from '../lib/uuid'

/**
 * The page the farm actually uses every evening. If this does not stick with one
 * owner on one flock, phases 2–4 are wasted work (§10).
 *
 * There is no hen-count field. Active hens come from the bird ledger (§3.4).
 */
export function DailyEntry() {
  const [params, setParams] = useSearchParams()
  const batches = useBatches()
  const materials = useMaterials()

  const batchId = params.get('batch') ?? batches.data?.[0]?.id ?? ''
  const [date, setDate] = useState(today())

  const production = useProduction(batchId || undefined)
  const recordProduction = useRecordProduction(batchId)
  const voidProduction = useVoidProduction(batchId)
  const createFeedIssue = useCreateFeedIssue(batchId)

  const [eggs, setEggs] = useState('')
  const [broken, setBroken] = useState('')
  const [dirty, setDirty] = useState('')
  const [eggsId, setEggsId] = useState(() => clientUuid())

  const [materialId, setMaterialId] = useState('')
  const [feedQty, setFeedQty] = useState('')
  const [feedIds, setFeedIds] = useState(() => ({ issue: clientUuid(), movement: clientUuid() }))

  const columns: ReadonlyArray<Column<EggProductionRecord>> = [
    { key: 'date', header: 'Date', render: (r) => r.productionDate },
    { key: 'eggs', header: 'Eggs', numeric: true, render: (r) => <Count value={r.eggsProduced} /> },
    { key: 'broken', header: 'Broken', numeric: true, render: (r) => <Count value={r.eggsBroken} /> },
    { key: 'dirty', header: 'Dirty', numeric: true, render: (r) => <Count value={r.eggsDirty} /> },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.voidedAt ? (
          <span className="muted">voided</span>
        ) : (
          <button
            type="button"
            className="link-button"
            onClick={() => voidProduction.mutate({ rowId: r.id })}
            disabled={voidProduction.isPending}
          >
            Void
          </button>
        ),
    },
  ]

  if (batches.isPending) return <p className="muted">Loading…</p>
  if (!batches.data || batches.data.length === 0) {
    return <EmptyState title="No batches yet." hint="Create a batch before entering a day's data." />
  }

  return (
    <div className="page">
      <header className="page-head">
        <h2>Daily entry</h2>
        <label className="inline-field">
          <span>Batch</span>
          <select
            value={batchId}
            onChange={(event) => setParams({ batch: event.target.value }, { replace: true })}
          >
            {batches.data.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
              </option>
            ))}
          </select>
        </label>
      </header>

      <EntryGrid
        title="Eggs collected"
        description="One live entry per batch per day. Correcting a day voids the entry and records a replacement."
        onSubmit={() =>
          recordProduction.mutate(
            {
              batchId,
              productionDate: date,
              eggsProduced: Number(eggs || '0'),
              eggsBroken: Number(broken || '0'),
              eggsDirty: Number(dirty || '0'),
              clientUuid: eggsId,
            },
            {
              onSuccess: () => {
                setEggs('')
                setBroken('')
                setDirty('')
                setEggsId(clientUuid())
              },
            },
          )
        }
        submitLabel="Save eggs"
        busy={recordProduction.isPending}
        error={recordProduction.isError ? recordProduction.error.message : null}
        success={recordProduction.isSuccess ? 'Saved.' : null}
      >
        <DateCell label="Date" value={date} onChange={setDate} hint="Back-dating is allowed" />
        <NumericCell label="Eggs collected" value={eggs} onChange={setEggs} integer />
        <NumericCell label="Broken" value={broken} onChange={setBroken} integer />
        <NumericCell label="Dirty" value={dirty} onChange={setDirty} integer />
      </EntryGrid>

      <EntryGrid
        title="Feed issued"
        description="Records the quantity and the outbound stock movement. Feed cost is entered on the Costs page until Phase 2 wires purchases to the weighted average."
        onSubmit={() =>
          createFeedIssue.mutate(
            {
              batchId,
              materialId,
              issueDate: date,
              qty: feedQty,
              clientUuid: feedIds.issue,
              movementClientUuid: feedIds.movement,
            },
            {
              onSuccess: () => {
                setFeedQty('')
                setFeedIds({ issue: clientUuid(), movement: clientUuid() })
              },
            },
          )
        }
        submitLabel="Save feed"
        busy={createFeedIssue.isPending}
        error={createFeedIssue.isError ? createFeedIssue.error.message : null}
        success={createFeedIssue.isSuccess ? 'Saved.' : null}
      >
        <label className="cell">
          <span className="cell-label">Material</span>
          <select
            className="cell-input"
            value={materialId}
            onChange={(event) => setMaterialId(event.target.value)}
            required
          >
            <option value="">Select…</option>
            {(materials.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.uom})
              </option>
            ))}
          </select>
          {materials.data?.length === 0 ? (
            <span className="cell-hint">
              No feed materials yet. Add one in <Link to="/settings">Settings</Link>.
            </span>
          ) : null}
        </label>
        <NumericCell label="Quantity" value={feedQty} onChange={setFeedQty} />
      </EntryGrid>

      <section>
        <h3>Recent production</h3>
        <DataTable
          rows={(production.data ?? []).slice(-30).reverse()}
          columns={columns}
          rowKey={(r) => r.id}
          rowClassName={(r) => (r.voidedAt ? 'voided' : undefined)}
          empty={<EmptyState title="No eggs recorded for this batch yet." />}
        />
      </section>
    </div>
  )
}
